/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, TextField, MenuItem,
  Button, Typography, Divider, Stack,
  IconButton, Radio, RadioGroup, FormControlLabel, CircularProgress,
  Alert,
  Snackbar,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { CicaafAPI } from 'api/cicaaf/cicaaf.api';
import TextFieldDataCustom from 'components/shared/textfiel_data_custom';
import { ROUTES } from 'routes/constants';
import { formatDateToDisplay } from 'utils/date_utils';

interface IProduct {
  product_uuid: string;
  product_name: string;
  product_type: string;
}

interface IProductionData {
  products: Array<{
    product_uuid: string;
    product_name: string;
    product_type: string;
    commercialized_value: number;
    commercialized_volume: number;
    measurement_unit: string;
  }>;
  vegetal_license: {
    has_sanitary_license: boolean;
    license_number?: string;
    issuing_agency?: string;
    issue_date?: string;
  };
  animal_inspection: {
    has_inspection_register: boolean;
    inspection_seals?: Array<{
      inspection_seal: string;
      register_number: string;
      issue_date: string;
    }>;
  };
}
interface IProdutoForm {
  product_uuid: string;
  product_name: string;
  product_type: string;
  commercialized_value: number;
  commercialized_volume: number;
  measurement_unit: string;
  has_sanitary_license: boolean;
  vegetal_license?: {
    license_number: string;
    issuing_agency: string;
    issue_date: string;
  };
  has_inspection_register: boolean;
  inspection_seals: Array<{
    inspection_seal: string;
    register_number: string;
    issue_date: string;
  }>;
  showSeloForm: boolean;
}

export enum EUnidadeMedida {
  KG = "KG",
  TON = "TON",
  L = "L",
  UN = "UN",
  CX = "CX",
  SC = "SC"
}


export enum CicaafSanitaryAgencyEnum {
  MUNICIPAL_HEALTH_SECRETARIAT = 'SECRETARIA SAÚDE MUNICÍPIO',
  STATE_HEALTH_SECRETARIAT = 'SECRETARIA SAÚDE ESTADO',
}

export enum ESeloInspecao {
  SIM = "Serviço de Inspeção Municipal – SIM",
  SISE = "Serviço de Inspeção Estadual – SISE",
  SIF = "Serviço de Inspeção Federal – SIF",
  SISBI = "Sistema Brasileiro de Inspeção de Produtos de Origem Animal – SISBI",
  SUSAF_MT = "Sistema Estadual Unificado de Sanidade Agroindustrial Familiar – SUSAF MT",
  SIAPP = "Serviço de Inspeção Agroindustrial de Pequeno Porte – SIAPP"
}
const UNIDADES_MEDIDA = Object.entries(EUnidadeMedida).map(([value, label]) => ({ label, value }));

const ORGAOS_EMISSORES = [
  { value: CicaafSanitaryAgencyEnum.MUNICIPAL_HEALTH_SECRETARIAT, label: 'SECRETARIA SAÚDE MUNICÍPIO' },
  { value: CicaafSanitaryAgencyEnum.STATE_HEALTH_SECRETARIAT, label: 'SECRETARIA SAÚDE ESTADO' }
];


const TIPOS_SELO = Object.entries(ESeloInspecao).map(([key, label]) => ({ key, label }));

// Exported pure helpers for testability (no runtime behavior change)
export const validateProductCommercialization = (formData: any[]): string | true => {
  for (const produto of formData) {
    if (produto.commercialized_value < 0 || isNaN(produto.commercialized_value)) {
      return 'Valor comercializado deve ser maior ou igual a zero.';
    }
    if (produto.measurement_unit !== 'KG') {
      return `Produto "${produto.product_name}" deve ter unidade de medida "KG".`;
    }
  }
  return true;
};

export const validateSanitaryLicense = (formData: any[]): string | true => {
  const temLicencaSanitariaAtiva = formData.some(p => p.has_sanitary_license);
  const vegetalDataComLicenca = formData.find(p => p.has_sanitary_license && p.vegetal_license);

  if (temLicencaSanitariaAtiva) {
    if (
      !vegetalDataComLicenca?.vegetal_license?.license_number ||
      !vegetalDataComLicenca?.vegetal_license?.issuing_agency ||
      !vegetalDataComLicenca?.vegetal_license?.issue_date
    ) {
      return 'Ao marcar "Possui alvará sanitário: Sim", o Número do registro, Órgão emissor e Data de emissão são obrigatórios.';
    }
  }
  return true;
};

export const formatCurrency = (value: number) => {
  return (Number(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const addProductToForm = (availableProducts: IProduct[], formData: IProdutoForm[], selectedProduct: string) => {
  if (!selectedProduct) return formData;
  const product = availableProducts.find(p => p.product_uuid === selectedProduct);
  if (product && !formData.some(p => p.product_uuid === selectedProduct)) {
    return [...formData, {
      product_uuid: product.product_uuid,
      product_name: product.product_name,
      product_type: product.product_type,
      commercialized_value: 0,
      commercialized_volume: 0,
      measurement_unit: 'KG',
      has_sanitary_license: false,
      has_inspection_register: false,
      inspection_seals: [],
      showSeloForm: false
    }];
  }
  return formData;
};

export const removeProductByIndex = (formData: IProdutoForm[], index: number) => {
  return formData.filter((_, i) => i !== index);
};

export const updateProductField = (formData: IProdutoForm[], index: number, campo: keyof IProdutoForm, valor: any) => {
  const novos = [...formData];
  (novos[index] as any)[campo] = valor;
  return novos;
};

export const updateVegetalLicenseField = (formData: IProdutoForm[], produtoIndex: number, campo: keyof NonNullable<IProdutoForm['vegetal_license']>, valor: string) => {
  const novos = [...formData];
  const produto = novos[produtoIndex];
  if (!produto.vegetal_license) {
    produto.vegetal_license = { license_number: '', issuing_agency: '', issue_date: '' } as any;
  }
  (produto.vegetal_license as any)[campo] = valor;
  return novos;
};

export const parseNumericFromCurrencyInput = (value: string) => {
  if (!value) return 0;
  const cleaned = value.replace(/[^0-9.,-]/g, '').trim();

  if (cleaned.indexOf('.') > -1 && cleaned.indexOf(',') > -1) {
    const noThousands = cleaned.replace(/\./g, '');
    const normalized = noThousands.replace(/,/g, '.');
    return Number(normalized) || 0;
  }

  if (cleaned.indexOf(',') > -1 && cleaned.indexOf('.') === -1) {
    return Number(cleaned.replace(/,/g, '.')) || 0;
  }

  return Number(cleaned) || 0;
};

export const parseCurrencyInputToReais = (value: string) => {
  if (!value) return 0;
  const digits = value.replace(/\D/g, '');
  if (!digits) return 0;
  return Number(digits) / 100;
};

export const toggleSeloFormHelper = (formData: IProdutoForm[], produtoIndex: number) => {
  const novos = [...formData];
  novos[produtoIndex].showSeloForm = !novos[produtoIndex].showSeloForm;
  return novos;
};

export const addSeloHelper = (formData: IProdutoForm[], produtoIndex: number, novoSelo: { inspection_seal: string; register_number: string; issue_date: string; }) => {
  if (!novoSelo.inspection_seal || !novoSelo.register_number || !novoSelo.issue_date) return { formData, clearedNovoSelo: novoSelo };
  const novos = [...formData];
  novos[produtoIndex].inspection_seals.push({ ...novoSelo });
  novos[produtoIndex].showSeloForm = false;
  return { formData: novos, clearedNovoSelo: { inspection_seal: '', register_number: '', issue_date: '' } };
};

export const removeSeloHelper = (formData: IProdutoForm[], produtoIndex: number, seloIndex: number) => {
  const novos = [...formData];
  novos[produtoIndex].inspection_seals.splice(seloIndex, 1);
  return novos;
};

export const ProducaoComercializacaoCicaaf: React.FC = () => {
  const { uuid: collectionUuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [availableProducts, setAvailableProducts] = useState<IProduct[]>([]);
  const [formData, setFormData] = useState<IProdutoForm[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const [novoSelo, setNovoSelo] = useState({
    inspection_seal: '',
    register_number: '',
    issue_date: ''
  });
  const [dateError, setDateError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const handleAutocompleteChange = (event: any, newValue: IProduct | null) => {
    setSelectedProduct(newValue);
  };


  useEffect(() => {
    const loadInitialData = async () => {
      if (!collectionUuid) {
        setLoading(false);
        console.error("Collection UUID não encontrado na URL.");
        return;
      }

      setLoading(true);
      try {
        const rawProducts = await CicaafAPI.listAllProducts();

        const mappedProducts = rawProducts.map((p: any) => ({
          product_uuid: p.uuid,
          product_name: p.name,
          product_type: p.type
        }));
        setAvailableProducts(mappedProducts);

        const existingDataResponse = await CicaafAPI.getProductionComercialization(collectionUuid);
        const existingData: IProductionData = existingDataResponse.dados;

        if (existingData && existingData.products) {
          const formattedData = existingData.products.map(apiProd => {
            const isAnimal = apiProd.product_type.includes('ANIMAL');
            return {
              ...apiProd,
              commercialized_value: apiProd.commercialized_value ?? 0,
              commercialized_volume: apiProd.commercialized_volume ?? 0,
              measurement_unit: apiProd.measurement_unit ?? 'KG',
              has_sanitary_license: !isAnimal ? (existingData.vegetal_license?.has_sanitary_license ?? false) : false,
              vegetal_license: !isAnimal && existingData.vegetal_license?.has_sanitary_license ? {
                license_number: existingData.vegetal_license.license_number ?? '',
                issuing_agency: existingData.vegetal_license.issuing_agency ?? '',
                issue_date: existingData.vegetal_license.issue_date ?? '',
              } : undefined,
              has_inspection_register: isAnimal ? (existingData.animal_inspection?.has_inspection_register ?? false) : false,
              inspection_seals: isAnimal ? (existingData.animal_inspection?.inspection_seals ?? []) : [],
              showSeloForm: false,
            };
          });
          setFormData(formattedData);
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [collectionUuid]);

  useEffect(() => {
    const savedData = localStorage.getItem('formData');
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
  }, []);






  const handleSalvar = async () => {
    if (!collectionUuid) {
      console.error("Não é possível salvar, UUID da coleção não encontrado.");
      showAlert("Erro: UUID da coleção não encontrado.", 'error');
      return;
    }

    try {
      localStorage.setItem('formData', JSON.stringify(formData));
    } catch (error) {
      console.error("Erro ao salvar no localStorage", error);
      showAlert("Erro ao salvar os dados no navegador.", 'warning');
    }

    const licenseValidation = validateSanitaryLicense(formData);
    if (licenseValidation !== true) {
      showAlert(licenseValidation, 'error');
      return;
    }

    const productValidation = validateProductCommercialization(formData);
    if (productValidation !== true) {
      showAlert(productValidation, 'error');
      return;
    }

    setSaving(true);

    try {
      const vegetalData = formData.find(p => p.has_sanitary_license && p.vegetal_license);

      const payload = {
        products: formData.map(prod => ({
          product_uuid: prod.product_uuid,
          commercialized_value: parseFloat(prod.commercialized_value.toString()),
          commercialized_volume: Number(prod.commercialized_volume),
          measurement_unit: prod.measurement_unit
        })),
        vegetal_license: {
          has_sanitary_license: formData.some(p => p.has_sanitary_license),
          license_number: vegetalData?.vegetal_license?.license_number ?? undefined,
          issuing_agency: vegetalData?.vegetal_license?.issuing_agency ?? undefined,
          issue_date: vegetalData?.vegetal_license?.issue_date ?? undefined
        },
        animal_inspection: {
          has_inspection_register: formData.some(p => p.has_inspection_register),
          inspection_seals: formData.flatMap(p => p.inspection_seals ?? [])
        }
      };
      await CicaafAPI.updateProductionComercialization(collectionUuid, payload);

      showAlert('Dados salvos com sucesso!', 'success');
      navigate(ROUTES.INFORMAR_DIFICULDADES_CICAAF.replace(':uuid', collectionUuid));
    } catch (error) {
      console.error('Erro ao salvar:', error);
      showAlert('Ocorreu um erro ao salvar os dados. Tente novamente.', 'error');
    } finally {
      setSaving(false);
    }
  };


  const adicionarProduto = () => {
    if (!selectedProduct) return;
    if (!formData.some(p => p.product_uuid === selectedProduct.product_uuid)) {
      setFormData([...formData, {
        product_uuid: selectedProduct.product_uuid,
        product_name: selectedProduct.product_name,
        product_type: selectedProduct.product_type,
        commercialized_value: 0,
        commercialized_volume: 0,
        measurement_unit: 'KG',
        has_sanitary_license: false,
        has_inspection_register: false,
        inspection_seals: [],
        showSeloForm: false
      }]);
    }
    setSelectedProduct(null);
  };

  const removerProduto = (index: number) => {
    setFormData(removeProductByIndex(formData, index));
  };

  const atualizarProduto = (index: number, campo: keyof IProdutoForm, valor: any) => {
    setFormData(updateProductField(formData, index, campo, valor));
  };

  const atualizarLicencaVegetal = (produtoIndex: number, campo: keyof NonNullable<IProdutoForm['vegetal_license']>, valor: string) => {
    setFormData(updateVegetalLicenseField(formData, produtoIndex, campo, valor));
  };


  const handleValorChange = (index: number, value: string) => {
    const reaisValue = parseCurrencyInputToReais(value);
    atualizarProduto(index, 'commercialized_value', reaisValue);
  };




  const toggleSeloForm = (produtoIndex: number) => {
    setFormData(toggleSeloFormHelper(formData, produtoIndex));
    setDateError(false);
    setNovoSelo({ inspection_seal: '', register_number: '', issue_date: '' });
  };

  const adicionarSelo = (produtoIndex: number) => {
    if (dateError) return;
    const { formData: novos, clearedNovoSelo } = addSeloHelper(formData, produtoIndex, novoSelo);
    setFormData(novos);
    setNovoSelo(clearedNovoSelo);
    setDateError(false);
  };

  const removerSelo = (produtoIndex: number, seloIndex: number) => {
    setFormData(removeSeloHelper(formData, produtoIndex, seloIndex));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }
  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="CICAAF - Produção e Comercialização" showBackButton={true} />
      <Box width='97%' display='flex' flexDirection='column' justifyContent='center' alignItems='center' margin='2rem'>
        <ContainerForm title='Produção e Comercialização' subtitle='Informe os dados de produção e comercialização.' width='1152px'>
          <Box display="flex" alignItems="center" gap={2} mb={4}>
            <Autocomplete<IProduct>
              fullWidth
              options={availableProducts.filter(prod => !formData.some(p => p.product_uuid === prod.product_uuid))}
              getOptionLabel={(option) => `${option.product_name} (${option.product_type})`}
              value={selectedProduct}
              onChange={handleAutocompleteChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione um produto"
                  placeholder="Digite para buscar..."
                />
              )}
              filterOptions={(options, { inputValue }) => {
                const searchText = (inputValue || '').toLowerCase().trim();
                if (searchText.length < 2) return options;
                return options.filter((option) => {
                  const productName = option.product_name.toLowerCase();
                  const productType = option.product_type.toLowerCase();
                  return productName.includes(searchText) || productType.includes(searchText);
                });
              }}
              noOptionsText="Nenhum produto encontrado"
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={adicionarProduto} disabled={!selectedProduct}>
              Adicionar Produto
            </Button>
          </Box>

          {formData.map((produto, produtoIndex) => {
            const valorFormatado = formatCurrency(produto.commercialized_value);
            const isAnimal = produto.product_type.includes('ANIMAL');

            return (
              <Box key={produto.product_uuid} mb={4} p={3} border="1px solid #eee" borderRadius={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">{produto.product_name}</Typography>
                  <IconButton onClick={() => removerProduto(produtoIndex)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </Box>
                <Typography color="text.secondary" gutterBottom>{produto.product_type}</Typography>

                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>Valor comercializado no último ano *</Typography>
                  <TextField
                    value={valorFormatado}
                    onChange={(e) => handleValorChange(produtoIndex, e.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Box>

                <Box mt={2}>
                  <TextField
                    value={produto.commercialized_volume ?? ''}
                    label="Volume comercializado no último ano"
                    onChange={(e) => {
                      const value = e.target.value === '' ? undefined : Number(e.target.value);
                      if (value === undefined || value >= 0) {
                        atualizarProduto(produtoIndex, 'commercialized_volume', value);
                      }
                    }}
                    onInput={(e: any) => {
                      const target = e.target || e.currentTarget;
                      if (!target || typeof target.value !== 'string') return;
                      // Remove non-numeric characters except decimal point
                      target.value = target.value.replace(/[^\d.]/g, '');
                      // Prevent multiple decimal points
                      const decimalCount = (target.value.match(/\./g) || []).length;
                      if (decimalCount > 1) {
                        target.value = target.value.replace(/\.(?=.*\.)/g, '');
                      }
                    }}
                    fullWidth
                    inputProps={{ inputMode: 'decimal' }}
                    sx={{
                      '& input[type=number]::-webkit-outer-spin-button,& input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                    }}
                  />

                </Box>

                <Box mt={2}>
                  <TextField select label="Unidade de medida" value={produto.measurement_unit} onChange={(e) => atualizarProduto(produtoIndex, 'measurement_unit', e.target.value)} fullWidth>
                    {UNIDADES_MEDIDA.map((unidade) => (
                      <MenuItem key={unidade.value} value={unidade.value}>
                        {unidade.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Divider sx={{ my: 3 }} />

                {isAnimal ? (
                  <>
                    <Typography variant="h6" gutterBottom>Informações Sanitárias - Produto Animal</Typography>
                    <Box mb={2}>
                      <Typography variant="subtitle1" gutterBottom>Possui registro de inspeção? *</Typography>
                      <RadioGroup row value={produto.has_inspection_register ? 'sim' : 'nao'} onChange={(e) => atualizarProduto(produtoIndex, 'has_inspection_register', e.target.value === 'sim')}>
                        <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                        <FormControlLabel value="nao" control={<Radio />} label="Não" />
                      </RadioGroup>
                    </Box>
                    {produto.has_inspection_register && (
                      <Box mt={3}>
                        <Typography variant="subtitle1" gutterBottom>Selos de inspeção *</Typography>
                        {produto.inspection_seals.map((selo, seloIndex) => (
                          <Box key={seloIndex} p={2} mb={3} bgcolor="#f5f5f5" borderRadius={1}>
                            <Box justifyContent="space-between" display="flex" alignItems="center" mb={2}>
                              <Typography variant="subtitle2">Selo {seloIndex + 1}</Typography>
                              <IconButton size="small" onClick={() => removerSelo(produtoIndex, seloIndex)}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                            <Stack spacing={2}>
                              <TextField label="Tipo de selo *" value={selo.inspection_seal} InputProps={{ readOnly: true }} fullWidth />
                              <TextField label="Número de registro *" value={selo.register_number} InputProps={{ readOnly: true }} fullWidth />
                              <TextField label="Data de emissão *" value={formatDateToDisplay(selo.issue_date)} InputProps={{ readOnly: true }} fullWidth />
                            </Stack>
                          </Box>
                        ))}
                        {!produto.showSeloForm && (
                          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => toggleSeloForm(produtoIndex)} sx={{ mt: 2 }}>Adicionar Selo</Button>
                        )}
                        {produto.showSeloForm && (
                          <Box mt={2} p={2} border="1px dashed #ccc" borderRadius={1}>
                            <Typography variant="subtitle2" gutterBottom>Adicionar Selo</Typography>
                            <Stack spacing={2}>
                              <TextField
                                select
                                label="Tipo de selo *"
                                value={novoSelo.inspection_seal}
                                onChange={(e) => setNovoSelo({ ...novoSelo, inspection_seal: e.target.value })}
                                fullWidth
                              >
                                {TIPOS_SELO.map((tipo) => (
                                  <MenuItem key={tipo.key} value={tipo.key}>
                                    {tipo.label}
                                  </MenuItem>
                                ))}
                              </TextField>

                              <TextField label="Número de registro *" placeholder="Ex: 12345/2024" value={novoSelo.register_number} onChange={(e) => setNovoSelo({ ...novoSelo, register_number: e.target.value })} fullWidth />
                              <TextFieldDataCustom
                                label="Data de emissão *"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={novoSelo.issue_date}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  setNovoSelo({ ...novoSelo, issue_date: e.target.value });

                                  if (inputValue) {
                                    const dateParts = inputValue.split('-');
                                    if (dateParts.length === 3 && dateParts[0] && dateParts[1] && dateParts[2]) {
                                      const yearStr = dateParts[0];
                                      const year = Number(yearStr);
                                      if (yearStr.length !== 4 || year < 1900) {
                                        setDateError(true);
                                        return;
                                      }
                                    }
                                  }
                                  setDateError(false);
                                }}
                                error={dateError}
                                helperText={dateError ? 'Data Inválida' : undefined}
                                fullWidth
                              />
                              <Box display="flex" justifyContent="flex-end" gap={1}>
                                <Button variant="outlined" onClick={() => toggleSeloForm(produtoIndex)}>Cancelar</Button>
                                <Button variant="contained" onClick={() => adicionarSelo(produtoIndex)} disabled={!novoSelo.inspection_seal || !novoSelo.register_number || !novoSelo.issue_date || dateError}>Adicionar</Button>
                              </Box>
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom>Informações Sanitárias - Produto Vegetal</Typography>
                    <Box mb={2}>
                      <Typography variant="subtitle1" gutterBottom>Possui alvará sanitário? *</Typography>
                      <RadioGroup row value={produto.has_sanitary_license ? 'sim' : 'nao'} onChange={(e) => atualizarProduto(produtoIndex, 'has_sanitary_license', e.target.value === 'sim')}>
                        <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                        <FormControlLabel value="nao" control={<Radio />} label="Não" />
                      </RadioGroup>
                    </Box>
                    {produto.has_sanitary_license && (
                      <Stack spacing={2}>
                        <TextField label="Número de registro do alvará sanitário *" value={produto.vegetal_license?.license_number ?? ''} onChange={(e) => atualizarLicencaVegetal(produtoIndex, 'license_number', e.target.value)} fullWidth />
                        <TextField
                          select
                          label="Órgão emissor *"
                          value={produto.vegetal_license?.issuing_agency ?? ''}
                          onChange={(e) =>
                            atualizarLicencaVegetal(produtoIndex, 'issuing_agency', e.target.value)
                          }
                          fullWidth
                        >
                          {ORGAOS_EMISSORES.map((orgao) => (
                            <MenuItem key={orgao.value} value={orgao.value}>
                              {orgao.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextFieldDataCustom
                          label="Data de emissão *"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          value={produto.vegetal_license?.issue_date ?? ''}
                          onChange={(e) => atualizarLicencaVegetal(produtoIndex, 'issue_date', e.target.value)}
                          fullWidth
                        />
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            );
          })}

          <Box display="flex" justifyContent="center" gap={3} mt={4}>
            <Button variant="outlined" onClick={() => window.history.back()} sx={{ minWidth: 120 }}>Voltar</Button>
            <Button variant="contained" color="primary" onClick={handleSalvar} disabled={formData.length === 0 || saving} startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} sx={{ minWidth: 180 }}>
              {saving ? 'Salvando...' : 'Salvar Dados'}
            </Button>
          </Box>
        </ContainerForm>
      </Box>
      <Snackbar
        open={alertOpen}
        autoHideDuration={3000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {alertMessage}
        </Alert>
      </Snackbar>

    </MenuProvider>
  );
};
